import { useCookie } from 'nuxt/app'
import { jwtDecode } from 'jwt-decode'
import type { UserJWT } from '@/types/jwt'
import { useFulfillmentOrder } from '@/composables/useFulfillmentOrder'
import { defineNuxtPlugin, navigateTo, storeToRefs } from '#imports'
import { useGuestStore } from '~/store/guest'

export default defineNuxtPlugin((nuxtApp: any) => {
  const api = $fetch.create({
    async onRequest({ options }) {
      await nuxtApp.runWithContext(async () => {
        // Ensure headers are a true Headers instance (this is necessary because options.headers
        // may be a plain object, tuple array, or undefined—not normalized by Nitro before onRequest)
        options.headers = new Headers((options.headers as HeadersInit) || {})

        if ((options as any).skipAuth) {
          console.info('[api]: skipAuth flag set, skipping!')
          return
        }

        const employerAccessToken = useCookie('employer_access_token')
        if (employerAccessToken.value) {
          options.headers.set(
            'x-authorization-obo',
            `Bearer ${employerAccessToken.value}`,
          )
          return
        }

        const guestStore = useGuestStore()
        const { guest } = storeToRefs(guestStore)

        if (guest?.value?.token && !isTokenValid(guest.value.token)) {
          if (guestStore.isCorpPerkStore) {
            await guestStore.loginCorpPerkStoreGuestUser()
          } else {
            await guestStore.loginGuestUser()
          }
          // This avoids refreshing guest token but still sending an expired token
          // In cases where the auth header was explicity set
          if (options.headers.has('x-authorization-obo')) {
            options.headers.set(
              'x-authorization-obo',
              `Bearer ${guest.value.token}`,
            )
            console.info('[api]: guest token refreshed and header updated')
          }
        }

        const token = guest.value?.loggedInToken || guest.value?.token
        if (token && !options.headers.has('x-authorization-obo')) {
          options.headers.set('x-authorization-obo', `Bearer ${token}`)
        } else {
          console.info('[api]: token is set, skipping!')
        }
      })
    },
    async onResponseError({ response }) {
      if (response.status === 401) {
        const { guest, reset: logout, setLoggedInToken } = useGuestStore()
        if (guest.loggedInToken && !isTokenValid(guest.loggedInToken)) {
          setLoggedInToken('')
          return
        }

        // 401 likely occured during OTP verification
        if (!guest.token) {
          return
        }

        const { emailToken, clearEmailToken } = useFulfillmentOrder()
        if (emailToken.value && !isTokenValid(emailToken.value)) {
          clearEmailToken()
          return
        }

        logout()
        await nuxtApp.runWithContext(() =>
          navigateTo('/welcome', { external: true }),
        )
      }
    },
  })

  function isTokenValid(token: string): boolean {
    try {
      const { exp } = jwtDecode<UserJWT>(token)
      return exp * 1000 > Date.now() + 60_000
    } catch {
      return false
    }
  }

  // Expose to useNuxtApp().$api
  return {
    provide: {
      api,
    },
  }
})
